import { Driver, DriverHook } from "driver.js";
import { defaultTourConfig } from ".";

// Export the tour steps so they can be reused in BrandGrandTour
export const brandCampaignTourSteps = [
  {
    element: "#campaigns",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
          <div class="flex -space-x-2">
            <div class="w-6 h-6 bg-brand-200 rounded-full border-2 border-white flex items-center justify-center">
              <svg class="w-3 h-3 text-brand-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
              </svg>
            </div>
            <div class="w-6 h-6 bg-brand-300 rounded-full border-2 border-white flex items-center justify-center">
              <svg class="w-3 h-3 text-brand-700" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 17l6-6 4 4 8-8v3h2V4h-6v2h3l-7 7-4-4-6 6v2z"></path>
              </svg>
            </div>
            </div>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-base font-semibold text-foreground">Campaigns Management</span>
        </div>
      </div>`,
      description: `<p class="text-[12px] text-muted-foreground mt-1">Create all your brand campaigns here – from welcome offers to exclusive campaigns for email or SMS</p>
        </div>

        <div class="bg-brand-50 p-4 rounded-lg border-l-4 border-brand-400 flex items-start gap-3 shadow-sm">
            <svg class="w-6 h-6 text-brand-600 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <div class="flex-grow">
                <p class="text-[12px] text-brand-800 font-medium leading-snug mb-2">
                    Create and manage all your brand's promotional campaigns with ease. A campaign is at the heart of every promotion, allowing you to:
                </p>
                <ul class="list-disc list-inside text-[12px] text-brand-800 space-y-1">
                    <li>
                        <span class="font-medium">Customize Campaign Rules:</span> Set a campaign's discount value, specify which products it applies to, define minimum purchase requirements, and limit the maximum number of uses.
                    </li>
                    <li>
                        <span class="font-medium">Ensure Seamless Integration:</span> All campaign details are reflected directly on your Shopify account via an API, ensuring a smooth and real-time process.
                    </li>
                </ul>
            </div>
        </div>

        <div class="flex items-center gap-2 text-[12px] text-brand-600 font-medium">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span>Estimated time to create a campaign: 1 minute</span>
        </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#active-campaigns",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
        
          <div class="flex -space-x-2">
            <div class="w-6 h-6 bg-brand-200 rounded-full border-2 border-white flex items-center justify-center">
              <svg class="w-3 h-3 text-brand-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
              </svg>
            </div>
            <div class="w-6 h-6 bg-brand-300 rounded-full border-2 border-white flex items-center justify-center">
              <svg class="w-3 h-3 text-brand-700" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 17l6-6 4 4 8-8v3h2V4h-6v2h3l-7 7-4-4-6 6v2z"></path>
              </svg>
            </div>
            </div>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-base font-semibold text-foreground">Active Campaigns</span>
        </div>
      </div>`,
      description: `<div class="space-y-3">
        <p class="text-[12px] text-muted-foreground leading-relaxed">Here you can see all your active campaigns and their performance.</p>
        <div class="bg-brand-50 p-3 rounded-lg">
          <div class="flex items-center gap-2 mb-2">
            <p class="text-xs font-medium text-brand-800">Active Campaigns Features:</p>
          </div>
          <ul class="text-xs text-brand-700 space-y-3">
            <li class="flex items-start gap-3">
              <div class="w-6 h-6 bg-brand-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg class="w-3 h-3 text-brand-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
                </svg>
              </div>
              <div>
                <p class="font-medium">Campaign Performance</p>
                <p class="text-muted-foreground">Track clicks, conversions, and revenue</p>
              </div>
            </li>
            <li class="flex items-start gap-3">
              <div class="w-6 h-6 bg-brand-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg class="w-3 h-3 text-brand-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                </svg>
              </div>
              <div>
                <p class="font-medium">Analytics Dashboard</p>
                <p class="text-muted-foreground">View detailed metrics and insights</p>
              </div>
            </li>
            <li class="flex items-start gap-3">
              <div class="w-6 h-6 bg-brand-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg class="w-3 h-3 text-brand-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"></path>
                </svg>
              </div>
              <div>
                <p class="font-medium">Campaign Settings</p>
                <p class="text-muted-foreground">Edit campaign details and targeting</p>
              </div>
            </li>
          </ul>
        </div>
        <div class="flex items-center gap-2 text-xs text-brand-600">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>Monitor your campaigns to optimize performance</span>
        </div>
      </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#create-campaign",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
        
          <div class="flex -space-x-2">
            <div class="w-6 h-6 bg-brand-200 rounded-full border-2 border-white flex items-center justify-center">
              <svg class="w-3 h-3 text-brand-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 4v16m8-8H4"></path>
              </svg>
            </div>
            <div class="w-6 h-6 bg-brand-300 rounded-full border-2 border-white flex items-center justify-center">
              <svg class="w-3 h-3 text-brand-700" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 17l6-6 4 4 8-8v3h2V4h-6v2h3l-7 7-4-4-6 6v2z"></path>
              </svg>
            </div>
            </div>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-base font-semibold text-foreground">Create New Campaign</span>
        </div>
      </div>`,
      description: `<div class="space-y-3">
        <p class="text-[12px] text-muted-foreground leading-relaxed">Create new campaigns to promote your products and services.</p>
        <div class="bg-brand-50 p-3 rounded-lg">
          <div class="flex items-center gap-2 mb-2">
            <p class="text-xs font-medium text-brand-800">Campaign Creation Features:</p>
          </div>
          <ul class="text-xs text-brand-700 space-y-3">
            <li class="flex items-start gap-3">
              <div class="w-6 h-6 bg-brand-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg class="w-3 h-3 text-brand-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
                </svg>
              </div>
              <div>
                <p class="font-medium">Campaign Details</p>
                <p class="text-muted-foreground">Set name, description, and goals</p>
              </div>
            </li>
            <li class="flex items-start gap-3">
              <div class="w-6 h-6 bg-brand-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg class="w-3 h-3 text-brand-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                </svg>
              </div>
              <div>
                <p class="font-medium">Targeting Options</p>
                <p class="text-muted-foreground">Define audience and geographic targeting</p>
              </div>
            </li>
            <li class="flex items-start gap-3">
              <div class="w-6 h-6 bg-brand-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg class="w-3 h-3 text-brand-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"></path>
                </svg>
              </div>
              <div>
                <p class="font-medium">Budget & Schedule</p>
                <p class="text-muted-foreground">Set spending limits and campaign duration</p>
              </div>
            </li>
          </ul>
        </div>
        <div class="flex items-center gap-2 text-xs text-brand-600">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>Create targeted campaigns to maximize your ROI</span>
        </div>
      </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
];

const startCampaignTour = (
  tourDriver: Driver,
  onNextClick: DriverHook,
  onPrevClick: DriverHook
) => {
  tourDriver.setConfig(defaultTourConfig);
  // Here we set the unique steps for the FIRST tour
  tourDriver.setSteps(
    brandCampaignTourSteps.map((step) => ({
      ...step,
      popover: {
        ...step.popover,
        onNextClick: (element, step, config) => {
          onNextClick(element, step, config);
          setTimeout(() => {
            tourDriver.moveNext();
          }, 200);
        },
        onPrevClick: (element, step, config) => {
          onPrevClick(element, step, config);
          setTimeout(() => {
            tourDriver.movePrevious();
          }, 200);
        },
      },
    }))
  );
  tourDriver.drive();
};

export default startCampaignTour;
